// src/modules/leads/route-planner/useGoogleMapsLoader.js
import { useState, useEffect, useRef } from 'react';

const POLL_INTERVAL = 200;  // ms between checks
const MAX_WAIT = 15000;     // give up after 15 s

const useGoogleMapsLoader = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Already fully loaded with geometry
    if (window.google?.maps?.geometry) {
      setIsLoaded(true);
      return;
    }

    // ── Poll for google.maps to appear (covers PWA cache race conditions) ──
    // The load event may have already fired before we attached listeners,
    // so polling is the only reliable fallback on iOS PWA / cached pages.
    let pollTimer = null;
    let timeoutTimer = null;

    const startPolling = () => {
      const started = Date.now();
      pollTimer = setInterval(() => {
        if (!mountedRef.current) { clearInterval(pollTimer); return; }
        if (window.google?.maps?.geometry) {
          clearInterval(pollTimer);
          clearTimeout(timeoutTimer);
          setIsLoaded(true);
        } else if (Date.now() - started > MAX_WAIT) {
          clearInterval(pollTimer);
          // Don't set error here — the timeout handler below will do it
        }
      }, POLL_INTERVAL);
    };

    let existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

    if (existingScript && window.google?.maps) {
      // Maps loaded but without geometry — remove and reload with all libraries
      if (!window.google.maps.geometry) {
        existingScript.remove();
        delete window.google.maps;
        existingScript = null; // Don't reuse detached DOM ref — fall through to fresh load
      } else {
        setIsLoaded(true);
        return;
      }
    }

    if (existingScript && !window.google?.maps) {
      // Script tag exists but may have already loaded (PWA cache) —
      // attach listeners AND poll as fallback
      const handleLoad = () => {
        if (!mountedRef.current) return;
        if (window.google?.maps?.geometry) {
          clearInterval(pollTimer);
          clearTimeout(timeoutTimer);
          setIsLoaded(true);
        }
      };
      const handleError = () => {
        if (!mountedRef.current) return;
        clearInterval(pollTimer);
        clearTimeout(timeoutTimer);
        setError('Failed to load Google Maps');
      };
      existingScript.addEventListener('load', handleLoad);
      existingScript.addEventListener('error', handleError);

      // Start polling in case the load event already fired
      startPolling();

      timeoutTimer = setTimeout(() => {
        if (!mountedRef.current) return;
        clearInterval(pollTimer);
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
        if (!window.google?.maps?.geometry) {
          setError('Google Maps took too long to load. Please reload.');
        }
      }, MAX_WAIT);

      return () => {
        mountedRef.current = false;
        clearInterval(pollTimer);
        clearTimeout(timeoutTimer);
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
      };
    }

    // Load fresh
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setError('Google Maps API key is missing');
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (!mountedRef.current) return;
      clearInterval(pollTimer);
      clearTimeout(timeoutTimer);
      if (window.google?.maps) {
        setIsLoaded(true);
      } else {
        setError('Google Maps failed to initialize');
      }
    };

    script.onerror = () => {
      if (!mountedRef.current) return;
      clearInterval(pollTimer);
      clearTimeout(timeoutTimer);
      setError('Failed to load Google Maps API');
    };

    document.body.appendChild(script);

    // Poll as fallback even for fresh scripts (handles iOS quirks)
    startPolling();

    timeoutTimer = setTimeout(() => {
      if (!mountedRef.current) return;
      clearInterval(pollTimer);
      if (!window.google?.maps) {
        setError('Google Maps took too long to load. Please reload.');
      }
    }, MAX_WAIT);

    return () => {
      mountedRef.current = false;
      clearInterval(pollTimer);
      clearTimeout(timeoutTimer);
    };
  }, []);

  return { isLoaded, error };
};

export default useGoogleMapsLoader;
